import React, { useState, useRef } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import RecoveryWorkspace from './components/RecoveryWorkspace';
import { useAuth } from '../../contexts/AuthContext';
import PolicyUploader from './components/PolicyUploader';
import PolicyResults from './components/PolicyResults';
import PolicyHistory from './components/PolicyHistory';
import type { PolicyAnalysis } from './types';
import { api } from '../../lib/api';
import { PageHeader } from '../../components/common/PageHeader';
import { Alert, Card, CircularProgress, Tab, TabList, Tabs } from '@mui/joy';
import { FileText, CheckCircle, Briefcase } from '@phosphor-icons/react';

const PolicyCopilotApp: React.FC = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const recovery = searchParams.get('workspace') === 'recovery';
  const [currentAnalysis, setCurrentAnalysis] = useState<PolicyAnalysis | null>(null);
  const [selectionError, setSelectionError] = useState('');
  const [selectingId, setSelectingId] = useState<number | null>(null);
  const selectionRequest = useRef(0);
  const [loading, setLoading] = useState(false);

  // Launched from a customer or a job (?contact=/?job=) — the analysis belongs
  // to that record, so the uploader shouldn't ask who it is for.
  const contactParam = Number(searchParams.get('contact')) || null;
  const jobParam = Number(searchParams.get('job')) || null;

  const recent = useQuery({
    queryKey: ['policy-copilot-recent'],
    queryFn: async () => (await api.get<PolicyAnalysis[]>('/api/policy-copilot/analyses/recent/')).data,
    enabled: isAuthenticated,
  });
  const analyses = recent.data || [];

  // A job knows its customer, so ?job= alone is enough to link the analysis.
  const { data: jobContactId } = useQuery({
    queryKey: ['policyCopilotJobContact', jobParam],
    queryFn: async () => {
      // Retrieve uses JobDetailSerializer, which nests the whole contact
      // rather than returning its id — hand the picker the id either way.
      const response = await api.get<{ associated_contact?: { id: number } | number | null }>(
        `/api/jobs/${jobParam}/`
      );
      const contact = response.data.associated_contact;
      if (!contact) return null;
      return typeof contact === 'number' ? contact : contact.id;
    },
    enabled: Boolean(jobParam) && !contactParam,
    staleTime: 60000,
  });
  const routeContactId = contactParam ?? jobContactId ?? null;

  const handleAnalysisComplete = (analysis: PolicyAnalysis) => {
    selectionRequest.current += 1;
    setSelectingId(null);
    setSelectionError('');
    setCurrentAnalysis(analysis);
    void recent.refetch();
  };

  const handleSelectAnalysis = async (analysis: PolicyAnalysis) => {
    const requestId = ++selectionRequest.current;
    setSelectionError('');
    if (!analysis.summary && analysis.status === 'completed') {
      setSelectingId(analysis.id);
      try {
        const response = await api.get<PolicyAnalysis>(`/api/policy-copilot/analyses/${analysis.id}/`);
        if (requestId === selectionRequest.current) setCurrentAnalysis(response.data);
      } catch {
        if (requestId === selectionRequest.current) setSelectionError('Could not open this analysis. Select it again to retry.');
      } finally {
        if (requestId === selectionRequest.current) setSelectingId(null);
      }
    } else {
      setSelectingId(null);
      setCurrentAnalysis(analysis);
    }
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, width: '100%', minWidth: 0 }}>
      <PageHeader title="Policy Copilot" description="Upload a customer's policy, review the extracted details against the source, and keep the claim moving." />
      <Tabs value={recovery ? 'recovery' : 'policy'} onChange={(_, value) => {
        const next = new URLSearchParams(searchParams);
        if (value === 'recovery') next.set('workspace', 'recovery'); else next.delete('workspace');
        setSearchParams(next);
      }} sx={{ mb: 3 }}>
        <TabList aria-label="Policy Copilot workspace" sx={{ flexWrap: 'wrap' }}>
          <Tab value="policy">Policy review</Tab><Tab value="recovery">Insurance recovery</Tab>
        </TabList>
      </Tabs>
      {recovery ? <RecoveryWorkspace/> : <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(320px, .9fr) minmax(0, 1.1fr)' }, gap: 3 }}>
        <Box>
          <PolicyUploader
            onAnalysisComplete={handleAnalysisComplete}
            loading={loading}
            setLoading={setLoading}
            presetContactId={routeContactId}
            // Locked only once a customer actually resolved, so a stale ?job=
            // leaves the picker usable instead of empty and frozen.
            contactLocked={Boolean(routeContactId)}
          />
          
          {recent.isLoading && <Typography role="status" sx={{ mt: 3 }} startDecorator={<CircularProgress size="sm" />}>Loading recent analyses…</Typography>}
          {recent.isError && <Alert color="danger" sx={{ mt: 3 }} endDecorator={<Button variant="soft" color="danger" loading={recent.isFetching} onClick={() => recent.refetch()}>Retry</Button>}>Could not load recent analyses.</Alert>}
          {selectionError && <Alert color="danger" sx={{ mt: 2 }}>{selectionError}</Alert>}
          {selectingId !== null && <Typography role="status" sx={{ mt: 2 }}>Opening analysis…</Typography>}
          {analyses.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <PolicyHistory 
                analyses={analyses}
                onSelectAnalysis={handleSelectAnalysis}
                currentAnalysisId={currentAnalysis?.id}
              />
            </Box>
          )}
        </Box>

        <Box>
          {!currentAnalysis && <Card variant="outlined" sx={{ p: { xs: 2, md: 3 }, gap: 2 }}>
            <Typography level="title-lg">From policy to next steps</Typography>
            {[
              { icon: <FileText size={22} />, title: '1. Add the policy', text: 'Choose a customer and upload the PDF or clear photos of all policy pages.' },
              { icon: <CheckCircle size={22} />, title: '2. Review the details', text: 'Check coverage, limits, and deductibles against the original pages. Review anything flagged as uncertain.' },
              { icon: <Briefcase size={22} />, title: '3. Keep the job moving', text: 'Save reviewed values to the job, then track submissions, decisions, and payments in Insurance recovery.' },
            ].map(step => <Box key={step.title} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box sx={{ color: 'primary.plainColor', pt: .5 }}>{step.icon}</Box><Box><Typography level="title-sm">{step.title}</Typography><Typography level="body-sm" sx={{ color: 'text.secondary', mt: .5 }}>{step.text}</Typography></Box>
            </Box>)}
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Your review appears here. You can also reopen a recent analysis.</Typography>
          </Card>}
          {currentAnalysis && (
            // Keyed so picking another analysis from history clears the
            // save-to-job selection and its result banner.
            <PolicyResults key={currentAnalysis.id} analysis={currentAnalysis} jobId={jobParam} />
          )}
        </Box>
      </Box>}
    </Box>
  );
};

export default PolicyCopilotApp;
